/**
 * ATS calendar events — trigger + poll (used for interviewer busy overlay).
 * Contract matches KInterviewScheduler InterviewDetailsAPI + useWeekScheduleFetchV2.
 * Timezone strings are canonicalized like InterviewManualScheduler (`findTimeZone`)
 * so values such as `Asia/Calcutta` become API-accepted names (e.g. `Asia/Kolkata`).
 */
import { api, ApiError } from '@/api/request';
import { findTimeZone } from 'timezone-support';
import moment from 'moment-timezone';

/**
 * Statuses we let through to the UI. `'free'` lands here in the new
 * scheduler so the calendar can paint it as a dotted-border ghost
 * block (see `ManualCalendar.renderEventContent`). Legacy
 * KInterviewScheduler dropped `'free'` entirely; we intentionally
 * diverge for visibility while keeping it OUT of slot-generation —
 * `useBusyCalendarEvents.interviewerBusyForAlgorithm` filters
 * `evt.status === 'free'` so availability/pipeline math is unchanged.
 */
const ALLOWED_EVENTS_TO_SHOW_IN_UI = [
  'busy',
  'oof',
  'unknown',
  'free',
] as const;

// Polling cadence + cap. Total max wait = POLL_INTERVAL_MS * MAX_POLLS.
//   2s × 15 = 30s ← previous
//   3s × 30 = 90s ← current — gives slow BE / cold calendar caches
//   enough time to reach SUCCESS without immediately tripping the
//   timeout path. sch-28 saw 12 parallel cycles all stuck-PENDING in
//   a 30s window for 3 panel interviewers; lifting the cap dramatically
//   reduces "calendar empty + retry storm" UX while we file the BE-side
//   PENDING-forever bug.
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 30;

/** Legacy KInterviewScheduler `errorMap` — user-facing strings */
export const CALENDAR_EVENTS_ERROR_MESSAGES: Record<string, string> = {
  primary_recruiter_calendar_not_connected:
    'Primary recruiter needs to connect their calendar to fetch interviewer events',
  primary_recruiter_missing:
    'Add a primary recruiter to fetch interviewer events',
  primary_recruiter_domain_mismatch:
    'Add a primary recruiter from the organization domain to fetch interviewer events',
};

export interface CalendarEventParams {
  userIds: number[];
  startDate: string;
  endDate: string;
  timezone: string;
  /** Optional — poll + trigger match InterviewDetailsAPI when present */
  atsJobId?: number;
  /**
   * Optional (edit mode). When supplied, the BE tags THIS interview's
   * own pre/post buffer events with `buffer_kind` in the poll response
   * so the UI can pull them out of the busy feed. See swagger
   * `calendar_events/trigger_fetch#ats_interview_id`.
   */
  atsInterviewId?: number;
}

export interface CalendarEvent {
  id?: string;
  title?: string;
  start: string;
  end: string;
  user_id?: number;
  status?: string;
  /**
   * Present only when `ats_interview_id` was sent to `trigger_fetch` and
   * the BE identified this event as that interview's own pre/post buffer.
   */
  buffer_kind?: 'pre' | 'post';
  [key: string]: unknown;
}

export interface CalendarUserInfo {
  id: string | number;
  calendar_available?: boolean;
  [key: string]: unknown;
}

export type CalendarPollStatus = 'PENDING' | 'SUCCESS' | 'FAILED' | 'UNKNOWN';

export interface CalendarPollResult {
  status: CalendarPollStatus;
  events: CalendarEvent[];
  usersInfo: CalendarUserInfo[];
  /** ATS error codes or messages */
  errors: string[];
}

export interface CalendarEventsFetchResult {
  events: CalendarEvent[];
  usersInfo: CalendarUserInfo[];
}

export interface CalendarEventsFetchOptions {
  signal?: AbortSignal;
}

export class CalendarEventsFetchError extends Error {
  readonly errors: string[];

  readonly userMessage: string;

  constructor(message: string, errors: string[]) {
    super(message);
    this.name = 'CalendarEventsFetchError';
    this.errors = errors;
    const code = errors[0];
    this.userMessage =
      (code && CALENDAR_EVENTS_ERROR_MESSAGES[code]) ||
      (typeof code === 'string' && code.includes(':')
        ? (code.split(':')[1]?.trim() ?? code)
        : typeof code === 'string'
          ? code.replaceAll('_', ' ')
          : message);
  }
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  return new Promise((resolve, reject) => {
    const onAbort = (): void => {
      clearTimeout(tid);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const tid = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Maps IANA / moment aliases to the canonical zone name the ATS calendar API expects.
 * Ref: InterviewManualScheduler (`findTimeZone(moment.tz.guess())?.name`).
 */
export function normalizeTimeZoneForAtsCalendarApi(timezone: string): string {
  const trimmed = timezone?.trim();
  if (!trimmed || trimmed === 'local') {
    return findTimeZone(moment.tz.guess())?.name ?? moment.tz.guess();
  }
  return findTimeZone(trimmed)?.name ?? trimmed;
}

export async function triggerCalendarEventsFetch(
  params: CalendarEventParams,
  options?: CalendarEventsFetchOptions
): Promise<string> {
  throwIfAborted(options?.signal);
  const body: Record<string, unknown> = {
    from: params.startDate,
    to: params.endDate,
    time_zone: normalizeTimeZoneForAtsCalendarApi(params.timezone),
    user_ids: params.userIds,
  };
  // Coerce via `Number(...)` because callers occasionally hand us
  // string ids straight from `useParams()` / `useQueryState()`. The
  // numeric-positive guard rejects `NaN`, `0`, and `null`/`undefined`
  // without forcing every call site to coerce upstream.
  if (
    params.atsJobId !== null &&
    params.atsJobId !== undefined &&
    Number(params.atsJobId) > 0
  ) {
    body.ats_job_id = Number(params.atsJobId);
  }
  // Same numeric-positive guard as `ats_job_id` — edit-mode only. When
  // present the poll response flags this interview's buffer events with
  // `buffer_kind`.
  if (
    params.atsInterviewId !== null &&
    params.atsInterviewId !== undefined &&
    Number(params.atsInterviewId) > 0
  ) {
    body.ats_interview_id = Number(params.atsInterviewId);
  }

  try {
    const res = await api.post<{ fetch_uuid?: string }>(
      'calendar_events/trigger_fetch',
      body,
      { signal: options?.signal }
    );
    return res.fetch_uuid ?? '';
  } catch (err) {
    if (err instanceof ApiError) {
      throw new CalendarEventsFetchError(
        err.code ?? 'calendar_trigger_failed',
        err.errors.length > 0 ? err.errors : ['calendar_trigger_failed']
      );
    }
    throw err;
  }
}

interface PollEnvelope {
  status?: string;
  results?: {
    events?: Record<string, unknown[]>;
    users_info?: unknown[];
  };
}

function unwrapPollEnvelope(res: unknown): PollEnvelope | null {
  if (res === null || typeof res !== 'object') {
    return null;
  }
  const top = res as Record<string, unknown>;
  const inner =
    top.data !== null &&
    typeof top.data === 'object' &&
    typeof (top.data as Record<string, unknown>).status === 'string'
      ? (top.data as PollEnvelope)
      : (top as PollEnvelope);
  if (typeof inner.status === 'string') {
    return inner;
  }
  return null;
}

function normalizeUserIdKey(id: number): string {
  return String(id);
}

function normalizeRawEvent(
  raw: Record<string, unknown>,
  fallbackUserId?: number
): CalendarEvent | null {
  const start = (raw.start_time ?? raw.start) as string | undefined;
  const end = (raw.end_time ?? raw.end) as string | undefined;
  if (!start || !end) {
    return null;
  }
  const title = (raw.subject ?? raw.title ?? 'Busy') as string;
  const id =
    typeof raw.id === 'string' || typeof raw.id === 'number'
      ? String(raw.id)
      : undefined;
  const user_id =
    typeof raw.user_id === 'number'
      ? raw.user_id
      : fallbackUserId !== undefined
        ? fallbackUserId
        : undefined;
  return {
    ...raw,
    id,
    start,
    end,
    title,
    ...(user_id !== undefined ? { user_id } : {}),
  };
}

function eventsListForUser(
  eventsObj: Record<string, unknown[]>,
  userId: number
): unknown[] {
  const k = normalizeUserIdKey(userId);
  return eventsObj[k] ?? eventsObj[String(userId)] ?? [];
}

function normalizeUsersInfo(raw: unknown[]): CalendarUserInfo[] {
  const out: CalendarUserInfo[] = [];
  for (const u of raw) {
    if (u === null || typeof u !== 'object') {
      continue;
    }
    const o = u as Record<string, unknown>;
    if ('id' in o) {
      out.push(o as CalendarUserInfo);
    }
  }
  return out;
}

function parseSuccessEvents(
  envelope: PollEnvelope,
  userIds: number[]
): CalendarEvent[] {
  const eventsObj = (envelope.results?.events ?? {}) as Record<
    string,
    unknown[]
  >;
  const out: CalendarEvent[] = [];

  for (const uid of userIds) {
    const list = eventsListForUser(eventsObj, uid);
    for (const raw of list) {
      if (raw === null || typeof raw !== 'object') {
        continue;
      }
      const e = raw as Record<string, unknown>;
      if (
        'status' in e &&
        typeof e.status === 'string' &&
        !ALLOWED_EVENTS_TO_SHOW_IN_UI.includes(
          e.status as (typeof ALLOWED_EVENTS_TO_SHOW_IN_UI)[number]
        )
      ) {
        continue;
      }
      const norm = normalizeRawEvent(e, uid);
      if (norm) {
        out.push(norm);
      }
    }
  }
  return out;
}

/**
 * Single poll read — returns status so callers can stop on SUCCESS (even with zero events).
 */
export async function pollCalendarEventsByFetchUuid(
  fetchUuid: string,
  options?: {
    atsJobId?: number;
    userIds?: number[];
    signal?: AbortSignal;
  }
): Promise<CalendarPollResult> {
  throwIfAborted(options?.signal);
  const params: Record<string, string | number> = { fetch_uuid: fetchUuid };
  if (
    options?.atsJobId !== null &&
    options.atsJobId !== undefined &&
    options.atsJobId > 0
  ) {
    params.ats_job_id = options.atsJobId;
  }

  let res: unknown;
  try {
    // Don't auto-unwrap — this endpoint has a dual shape (legacy flat
    // array body vs. envelope) and the custom unwrapPollEnvelope below
    // handles both. callApi still throws ApiError on 4xx / errors-list.
    res = await api.get<unknown>('calendar_events', {
      params,
      signal: options?.signal,
      unwrapData: false,
    });
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        status: 'FAILED',
        events: [],
        usersInfo: [],
        errors: err.errors.length > 0 ? err.errors : [err.message],
      };
    }
    throw err;
  }

  // Legacy: flat array body → treat as settled success
  if (Array.isArray(res)) {
    const events = (res as unknown[])
      .map((item) =>
        item !== null && typeof item === 'object'
          ? normalizeRawEvent(item as Record<string, unknown>)
          : null
      )
      .filter((e): e is CalendarEvent => e !== null);
    return {
      status: 'SUCCESS',
      events,
      usersInfo: [],
      errors: [],
    };
  }

  const envelope = unwrapPollEnvelope(res);
  if (!envelope || !envelope.status) {
    return {
      status: 'UNKNOWN',
      events: [],
      usersInfo: [],
      errors: [],
    };
  }

  const st = envelope.status.toUpperCase();
  const userIds = options?.userIds ?? [];

  if (st === 'PENDING') {
    return {
      status: 'PENDING',
      events: [],
      usersInfo: [],
      errors: [],
    };
  }

  if (st === 'FAILED') {
    return {
      status: 'FAILED',
      events: [],
      usersInfo: [],
      errors: ['calendar_poll_failed'],
    };
  }

  if (st === 'SUCCESS') {
    const usersInfo = normalizeUsersInfo(
      (envelope.results?.users_info ?? []) as unknown[]
    );
    return {
      status: 'SUCCESS',
      events: parseSuccessEvents(envelope, userIds),
      usersInfo,
      errors: [],
    };
  }

  return {
    status: 'UNKNOWN',
    events: [],
    usersInfo: [],
    errors: [],
  };
}

/**
 * Trigger fetch and poll until SUCCESS (including zero events), FAILED, or max polls.
 * Honors AbortSignal (React Query cancellation).
 */
export async function fetchCalendarEventsWithPolling(
  params: CalendarEventParams,
  options?: CalendarEventsFetchOptions
): Promise<CalendarEventsFetchResult> {
  const signal = options?.signal;
  throwIfAborted(signal);

  const fetchUuid = await triggerCalendarEventsFetch(params, { signal });
  if (!fetchUuid) {
    return { events: [], usersInfo: [] };
  }

  const pollOptsBase = {
    atsJobId: params.atsJobId,
    userIds: params.userIds,
    signal,
  };

  for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
    throwIfAborted(signal);
    const poll = await pollCalendarEventsByFetchUuid(fetchUuid, pollOptsBase);

    if (poll.status === 'FAILED') {
      throw new CalendarEventsFetchError(
        poll.errors[0] ?? 'calendar_poll_failed',
        poll.errors.length > 0 ? poll.errors : ['calendar_poll_failed']
      );
    }

    if (poll.status === 'SUCCESS') {
      return { events: poll.events, usersInfo: poll.usersInfo };
    }

    if (poll.status === 'PENDING') {
      await delay(POLL_INTERVAL_MS, signal);
      continue;
    }

    // UNKNOWN — retry with backoff
    await delay(POLL_INTERVAL_MS, signal);
  }

  throwIfAborted(signal);
  const last = await pollCalendarEventsByFetchUuid(fetchUuid, pollOptsBase);
  if (last.status === 'FAILED') {
    throw new CalendarEventsFetchError(
      last.errors[0] ?? 'calendar_poll_failed',
      last.errors.length > 0 ? last.errors : ['calendar_poll_failed']
    );
  }
  if (last.status === 'SUCCESS') {
    return { events: last.events, usersInfo: last.usersInfo };
  }

  throw new CalendarEventsFetchError('calendar_poll_timeout', [
    'calendar_poll_timeout',
  ]);
}
